import {
  string,
  func,
  oneOf,
  shape,
  bool,
  object,
  number,
  array,
} from 'prop-types';
import { mergeDeepRight } from 'ramda';
import React, { Component } from 'react';

import { VIDEO } from '@ezugi/constants';

import { VIDEO_CONTAINER_ID } from '../../constants';
import { VideoWrapper } from './style';

const {
  ON_ERROR,
  ON_LOADING,
  ON_META_DATA,
  ON_MUTE,
  ON_PAUSE,
  ON_PLAY,
  ON_READY,
  ON_START_BUFFERING,
  ON_STATS,
  ON_STOP_BUFFERING,
  ON_STREAM_INFO,
  ON_STREAM_INFO_UPDATE,
  ON_UNMUTE,
  ON_VOLUME_CHANGE,
  ON_WARNING,
} = VIDEO;

class VideoPlayer extends Component {

  static pause() {
    if (VideoPlayer.isReady()) {
      VideoPlayer.player.pause();
    }
  }

  static play() {
    if (VideoPlayer.isReady()) {
      VideoPlayer.player.play();
    }
  }

  static mute() {
    if (VideoPlayer.isReady()) {
      VideoPlayer.player.mute();
    }
  }

  static unmute() {
    if (VideoPlayer.isReady()) {
      VideoPlayer.player.unmute();
    }
  }

  static volume(value) {
    if (VideoPlayer.isReady()) {
      VideoPlayer.player.setVolume(value);
    }
  }

  static isReady() {
    return !!VideoPlayer.player;
  }

  static destroy() {
    VideoPlayer.player.destroy();
    VideoPlayer.player = null;
  }

  static getInstance() {
    return VideoPlayer.player;
  }

  constructor() {
    super();

    if (document.getElementById(VideoPlayer.playerId)) {
      this.props.onCatchError();
    }
  }

  componentDidMount() {
    this.createInstance();
  }

  static playerId = VIDEO_CONTAINER_ID;

  static PLAY_PAUSE_INTERVAL = 5 * 60 * 1000;

  static player = null;

  createInstance = () => {
    const {
      onInit,
      events,
      config,
      secure,
      selectedStream,
      streamEntries = [],
      startIndex = 0,
      secureOptions,
    } = this.props;

    if (VideoPlayer.player || !selectedStream) return;

    let source = {
      entries: [
        {
          bintu: {
            apiurl: selectedStream.h5live.rtmp.url,
            streamid: selectedStream.h5live.rtmp.streamname,
          },
        },
      ],
      startIndex: 0,
    };

    if (secure) {
      source = {
        entries: streamEntries,
        startIndex,
        options: secureOptions,
      };
    }

    const initialConfig = {
      source,
      playback: {
        autoplay: true,
        automute: true,
        muted: false,
      },
      events: {},
      style: {
        controls: false,
        width: '100%',
        height: '100%',
        interactive: true,
        keepFrame: true,
      },
    };

    // eslint-disable-next-line no-return-assign
    Object.keys(events).forEach((eventName) => (initialConfig.events[eventName] = events[eventName]));

    VideoPlayer.player = new window.NanoPlayer(VideoPlayer.playerId);
    VideoPlayer.player
      .setup(mergeDeepRight(initialConfig, config))
      .then(() => {
        onInit(VideoPlayer.player);
      })
      .catch(this.props.onCatchError);

    if (import.meta.env.MODE !== 'production') window.VideoPlayer = VideoPlayer;
  };

  render() {
    const { hideControls, configProps } = this.props;

    return (
      <VideoWrapper
        hideControls={hideControls}
        {...configProps}
        id={VideoPlayer.playerId}
      />
    );
  }

}

VideoPlayer.defaultProps = {
  onInit: () => {},
  onCatchError: () => {},
  events: {},
  config: {},
  hideControls: false,
};

VideoPlayer.propTypes = {
  secure: bool,
  selectedStream: object,
  streamEntries: array,
  startIndex: number,
  isHandheld: bool,
  tokenValue: string,
  tokenOptions: string,
  playPauseInterval: number,
  onInit: func,
  onCatchError: func,
  hideControls: bool,
  config: object,
  configProps: object,
  events: shape({
    [oneOf([
      ON_ERROR,
      ON_LOADING,
      ON_META_DATA,
      ON_MUTE,
      ON_PAUSE,
      ON_PLAY,
      ON_READY,
      ON_START_BUFFERING,
      ON_STATS,
      ON_STOP_BUFFERING,
      ON_STREAM_INFO,
      ON_STREAM_INFO_UPDATE,
      ON_UNMUTE,
      ON_VOLUME_CHANGE,
      ON_WARNING,
    ])]: func,
  }),
  secureOptions: object,
};

export default VideoPlayer;
